import { useEffect, useState, useRef } from "react";
import { Spacer } from "@/components/Spacer";

import { motion, useInView } from "framer-motion";

export default function Section04({ isMobile }: { isMobile: boolean }) {
  const getDesktopDescription = () => {
    return (
      <div
        className="whitespace-pre-line text-center font-pre-regular text-[#2E323A] desktop:text-[20px] text-[14px] leading-[150%]"
        style={{ letterSpacing: "-0.02em" }}
      >
        CIERA is an integrated medical AI system that replicates the clinical
        reasoning process of physicians.{"\n"} It combines specialized AI
        modules for medical data recognition, literature retrieval,{"\n"}
        evidence-based Q&A, and multilingual clinical language processing.{"\n"}
        These modules work in concert with a core diagnostic engine to deliver
        accurate and trustworthy health assessments,{"\n"} just like consulting
        a real doctor
      </div>
    );
  };

  const getMobileDescription = () => {
    return (
      <div
        className="whitespace-pre-line text-center font-pre-regular text-[#2E323A] desktop:text-[20px] text-[14px] leading-[150%]"
        style={{ letterSpacing: "-0.02em" }}
      >
        CIERA is an integrated medical AI system that replicates{"\n"} the
        clinical reasoning process of physicians.{"\n"} It combines specialized
        AI modules for medical data{"\n"} recognition, literature retrieval,
        evidence-based Q&A, and{"\n"} multilingual clinical language processing.
        {"\n"}
        These modules work in concert with a core diagnostic{"\n"} engine to
        deliver accurate and trustworthy health{"\n"} assessments, just like
        consulting a real doctor
      </div>
    );
  };

  const titleRef = useRef(null);
  const isTitleInView = useInView(titleRef, { once: true, amount: 0.0 });

  const w01Ref = useRef(null);
  const isW01InView = useInView(w01Ref, { once: true, amount: 0.5 });

  const w02Ref = useRef(null);
  const isW02InView = useInView(w02Ref, { once: true, amount: 0.5 });

  const titleVariants = {
    hidden: {
      opacity: 0,
      scale: 1,
      rotate: 0,
      skew: 0,
      x: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      skew: 0,
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.1,
      },
    },
  };

  return (
    <div className="flex flex-col items-center desktop:py-[120px] desktop:px-[32px] py-[80px] px-[24px] bg-[#F5F6FA]">
      <motion.div
        id="title"
        className="flex flex-col items-center"
        ref={titleRef}
        initial="hidden"
        animate={isTitleInView ? "visible" : "hidden"}
        variants={titleVariants}
      >
        <div
          className="font-eng-01-bold text-center desktop:text-[40px] text-[24px] leading-[130%]"
          style={{
            letterSpacing: "-0.03em",
            background: "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Healthier AI Model – CIERA
        </div>
        <Spacer height={3} />
        <div
          className="text-center text-[#787C83] font-eng-regular desktop:text-[18px] text-[12px] leading-[150%]"
          style={{ letterSpacing: "-0.02em" }}
        >
          Clinical Intelligence for Evidence-based Reasoning & Assistance
        </div>

        <Spacer height={isMobile ? 16 : 32} />
        {isMobile ? getMobileDescription() : getDesktopDescription()}
      </motion.div>

      <Spacer height={isMobile ? 80 : 120} />

      <div className="desktop:flex desktop:w-full dekstop:flex-row gap-[24px] desktop:max-w-[960px] hidden">
        <WithoutCiera />

        <WithCiera />
      </div>

      <div className="w-full flex flex-col items-center max-w-[342px] desktop:hidden">
        <motion.div
          id="w-01"
          className="flex flex-col items-center"
          ref={w01Ref}
          initial="hidden"
          animate={isW01InView ? "visible" : "hidden"}
          variants={titleVariants}
        >
          <div
            className="font-pre-medium text-[24px] leading-[1.2em] text-[#999DA4]"
            style={{ letterSpacing: "-0.02em" }}
          >
            Without CIERA
          </div>

          <Spacer height={20} />
          <CieraCard01 isMobile={true} />
          <Spacer height={50} />
          <div
            className="font-pre-bold text-[24px] leading-[1.2em]"
            style={{
              letterSpacing: "-0.02em",
              background:
                "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            With CIERA
          </div>

          <Spacer height={20} />
          <CieraCard03 isMobile={true} />
        </motion.div>
        <Spacer height={50} />
        <motion.div
          id="w-02"
          className="flex flex-col items-center"
          ref={w02Ref}
          initial="hidden"
          animate={isW02InView ? "visible" : "hidden"}
          variants={titleVariants}
        >
          <div
            className="font-pre-medium text-[24px] leading-[1.2em] text-[#999DA4]"
            style={{ letterSpacing: "-0.02em" }}
          >
            Without CIERA
          </div>

          <Spacer height={20} />
          <CieraCard02 isMobile={true} />
          <Spacer height={50} />
          <div
            className="font-pre-bold text-[24px] leading-[1.2em]"
            style={{
              letterSpacing: "-0.02em",
              background:
                "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            With CIERA
          </div>

          <Spacer height={20} />
          <CieraCard04 isMobile={true} />
        </motion.div>
      </div>
    </div>
  );
}

const CieraCard01 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <div
      className="w-full desktop:h-[380px] h-[300px] rounded-[24px] bg-[#F5F6FA] flex flex-col items-center  justify-between pt-[32px] desktop:px-[18px] px-[8px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
    >
      <div
        className="font-eng-semibold text-[#4F4F4F] leading-[130%] desktop:text-[18px] text-[17px]"
        style={{ letterSpacing: "-0.05em" }}
      >
        Uniform, Non-Personalized Information
      </div>

      <img
        src={
          isMobile
            ? "assets/images/section-04-01-mobile-eng.png"
            : "assets/images/section-04-01-desktop-eng.png"
        }
        alt="CIERA 01"
        className="desktop:w-[430px] desktop:h-[276.5px] w-[335px] h-[214px]"
      />
    </div>
  );
};

const CieraCard02 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <div
      className="w-full desktop:h-[420px] h-[320px] rounded-[24px] bg-[#F5F6FA] flex flex-col items-center  justify-between pt-[32px] desktop:px-[0px] px-[18px] desktop:pb-[20px] pb-[16px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
    >
      <div
        className="font-eng-semibold text-[#4F4F4F] leading-[130%] desktop:text-[18px] text-[17px] text-center"
        style={{ letterSpacing: "-0.05em" }}
      >
        Fixed Question Flow and
        <br /> Predefined Choices
      </div>

      <img
        src={
          isMobile
            ? "assets/images/section-04-02-mobile-eng.png"
            : "assets/images/section-04-02-desktop-eng.png"
        }
        alt="CIERA 02"
        className="desktop:w-[430px] desktop:h-[292.5px] w-[315px] h-[214.5px]"
      />
    </div>
  );
};

const CieraCard03 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <motion.div
      className="w-full desktop:h-[380px] h-[300px] rounded-[24px] bg-[#ffffff] flex flex-col items-center justify-between pt-[32px] desktop:px-[0px] px-[18px] desktop:pb-[20px] pb-[16px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
      whileHover={{
        opacity: 1,
        scale: 1.02,
        rotate: 0,
        skew: 0,
        x: 0,
        y: 0,
        boxShadow: "0px 2px 20px 0px rgba(82, 98, 242, 0.2)",
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
        mass: 1,
        delay: 0,
      }}
    >
      <div
        className="font-eng-semibold text-[#000000] leading-[130%] desktop:text-[18px] text-[17px] text-center"
        style={{ letterSpacing: "-0.05em" }}
      >
        Health Assessment Modeled on
        <br /> Clinical Reasoning
      </div>

      <img
        src={
          isMobile
            ? "assets/images/section-04-03-mobile-eng.png"
            : "assets/images/section-04-03-desktop-eng.png"
        }
        alt="CIERA 03"
        className="desktop:w-[430px] desktop:h-[248px] w-[315px] h-[181.5px]"
      />
    </motion.div>
  );
};

const CieraCard04 = ({ isMobile = false }: { isMobile?: boolean }) => {
  return (
    <motion.div
      className="w-full desktop:h-[420px] h-[340px] rounded-[24px] bg-[#ffffff] flex flex-col items-center justify-between pt-[32px] desktop:px-[0px] px-[18px] desktop:pb-[16px] pb-[16px]"
      style={{ boxShadow: "2px 2px 20px 0px rgba(116, 116, 116, 0.1)" }}
      whileHover={{
        opacity: 1,
        scale: 1.02,
        rotate: 0,
        skew: 0,
        x: 0,
        y: 0,
        boxShadow: "0px 2px 20px 0px rgba(82, 98, 242, 0.2)",
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
        mass: 1,
        delay: 0,
      }}
    >
      <div
        className="font-eng-semibold text-[#000000] leading-[130%] desktop:text-[18px] text-[17px] text-center"
        style={{ letterSpacing: "-0.05em" }}
      >
        Free-Form Input with
        <br /> Automated Medical Feature Extraction
      </div>

      <video
        src="/assets/videos/section-04-04-eng.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="desktop:w-[430px] desktop:h-[293px] h-[214.5px]"
      />
    </motion.div>
  );
};

const WithoutCiera = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.0, once: true });

  const variants = {
    hidden: {
      opacity: 0,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.25,
      },
    },
  };

  return (
    <motion.div
      id="without-ciera"
      className="w-full flex flex-col items-center"
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
    >
      <div
        className="font-eng-01-semibold text-[24px] leading-[1.2em] text-[#999DA4]"
        style={{ letterSpacing: "-0.02em" }}
      >
        Without CIERA
      </div>

      <Spacer height={32} />
      <CieraCard01 isMobile={false} />
      <Spacer height={24} />
      <CieraCard02 isMobile={false} />
    </motion.div>
  );
};

const WithCiera = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.5, once: true });

  const variants = {
    hidden: {
      opacity: 0,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 100,
        mass: 1,
        delay: 0.25,
      },
    },
  };

  return (
    <motion.div
      id="with-ciera"
      className="w-full flex flex-col items-center"
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
    >
      <div
        className="font-eng-01-bold text-[24px] leading-[1.2em]"
        style={{
          letterSpacing: "-0.02em",
          background: "linear-gradient(90deg, #5464F2 17.01%, #39969E 83.1%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        With CIERA
      </div>

      <Spacer height={32} />
      <CieraCard03 isMobile={false} />

      <Spacer height={24} />
      <CieraCard04 isMobile={false} />
    </motion.div>
  );
};
